import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { recordings } from '@/api/recordings';
import type { RecordingRow } from '@/api/recordings';
import { cn } from '@/lib/cn';
import { Disc, X, ChevronDown } from 'lucide-react';
import { NewRecordingMenu } from '@/components/recordings/NewRecordingMenu';
import type { NewRecordingMenuExtraAction } from '@/components/recordings/NewRecordingMenu';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { useAuth } from '@/lib/auth-store';

function fmtDuration(ms: number | null): string {
  if (ms == null) return '';
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

interface Props {
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  disabled?: boolean;
  /**
   * Optional customer scope for the inline "new recording" menu. When
   * omitted, the server derives it from the caller's tenant (office-
   * manager / standard-user flow); super-admin / reseller-owner pickers
   * should pass an explicit customerId from the surrounding dialog so
   * creation doesn't fail with customer_id_required.
   */
  customerId?: string | null;
  /** Default folder for newly-created recordings. Optional. */
  folderId?: string | null;
  /**
   * Hide the inline "new recording" menu. Default: false. Set to `true`
   * from contexts where creating a new recording inline doesn't make
   * sense (e.g. read-only audits).
   */
  hideCreateMenu?: boolean;
  /**
   * Additional actions appended to the create menu. Exposed so parallel
   * branches (append/prepend, etc.) can add new creation modes without
   * modifying NewRecordingMenu or this component.
   */
  createMenuExtraActions?: NewRecordingMenuExtraAction[];
}

export function RecordingPicker({
  value,
  onChange,
  label,
  disabled = false,
  customerId,
  folderId,
  hideCreateMenu = false,
  createMenuExtraActions,
}: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const accessToken = useAuth((s) => s.accessToken);

  function handleSearchChange(v: string) {
    setSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(v), 250);
  }

  const { data: selectedData } = useQuery({
    queryKey: ['recording', value],
    queryFn: () => recordings.get(value!),
    enabled: !!value,
  });

  const { data: listData, isLoading } = useQuery({
    queryKey: ['recordings', 'picker', debouncedSearch],
    queryFn: () => recordings.list({ search: debouncedSearch || undefined, limit: 20 }),
    enabled: open,
  });

  const options: RecordingRow[] = listData?.rows ?? [];

  function selectRecording(id: string | null) {
    onChange(id);
    setOpen(false);
    setSearch('');
    setDebouncedSearch('');
  }

  function handleCreated(row: RecordingRow) {
    // Refresh the picker's own list view AND the shared 'recording' query
    // so the selected-label resolves immediately. Then auto-select.
    qc.invalidateQueries({ queryKey: ['recordings', 'picker'] });
    qc.invalidateQueries({ queryKey: ['recordings'] });
    qc.setQueryData(['recording', row.id], row);
    selectRecording(row.id);
  }

  const displayName = value
    ? (selectedData?.name ?? 'Loading…')
    : 'None';
  const displayDuration = value && selectedData ? fmtDuration(selectedData.duration_ms ?? null) : '';

  return (
    <div className="relative">
      {label && (
        <p className="mb-1 text-xs font-medium text-slate-600">{label}</p>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm transition-colors',
          'hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          open && 'border-slate-400',
        )}
      >
        <span className="flex items-center gap-2 truncate min-w-0">
          <Disc className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className={cn('truncate', !value && 'text-slate-400')}>
            {displayName}
          </span>
          {displayDuration && (
            <span className="shrink-0 font-mono text-[10px] text-slate-400 tabular-nums">
              {displayDuration}
            </span>
          )}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              role="button"
              aria-label="Clear recording"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onChange(null); } }}
              className="rounded p-0.5 text-slate-400 hover:text-slate-600"
            >
              <X className="h-3 w-3" />
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        </span>
      </button>

      {/* Inline preview of the currently-selected recording. The audioUrl
          carries a short-lived access token as a query param so the native
          <audio> element (which can't set Authorization headers) can stream
          the file. Uses the same pattern as RecordingDetail / VoicemailsPage. */}
      {value && selectedData && (
        <div className="mt-1.5">
          <AudioPlayer src={recordings.audioUrl(value, accessToken)} />
        </div>
      )}

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 p-2">
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search recordings…"
              className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-slate-400"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {/* None option */}
            <button
              type="button"
              onClick={() => selectRecording(null)}
              className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 border-b border-slate-100"
            >
              <X className="h-3 w-3" /> None
            </button>

            {isLoading ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400">Loading…</div>
            ) : options.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400">
                {debouncedSearch ? `No results for "${debouncedSearch}"` : 'No recordings found.'}
              </div>
            ) : (
              options.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => selectRecording(r.id)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50',
                    value === r.id && 'bg-blue-50 text-blue-700',
                  )}
                >
                  <Disc className="h-3 w-3 shrink-0 text-slate-300" />
                  <span className="flex-1 truncate font-medium">{r.name}</span>
                  <span className="shrink-0 font-mono text-[10px] text-slate-400">
                    {fmtDuration(r.duration_ms)}
                  </span>
                  {r.language && (
                    <span className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[9px] font-medium uppercase text-slate-500">
                      {r.language}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>

          {!hideCreateMenu && (
            <NewRecordingMenu
              customerId={customerId ?? null}
              folderId={folderId ?? null}
              onCreated={handleCreated}
              extraActions={createMenuExtraActions}
            />
          )}
        </div>
      )}
    </div>
  );
}
